<template>
  <div class="tab-content">
    <div class="mb-6">
      <h2 class="text-2xl font-bold text-gray-900 mb-1 flex items-center">
        <font-awesome-icon icon="cog" class="mr-3 text-blue-600" />
        Configuration
      </h2>
      <p class="text-gray-600 text-sm leading-relaxed">
        This is the default schedule configuration used by all time tables unless a specific time table has a custom
        configuration.
      </p>
    </div>

    <div v-if="loading" class="text-center py-12">
      <base-page-spinner type="spin" class-list="h-12"></base-page-spinner>
      <p class="text-gray-500 mt-4">Loading configuration…</p>
    </div>

    <div v-else class="space-y-4">
      <div v-if="errorMessage" class="rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-800">
        {{ errorMessage }}
      </div>

      <div class="rounded-xl border border-gray-200 bg-gray-50 p-4">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div class="text-sm text-gray-700">
            <div class="font-semibold">Default (General)</div>
          </div>
          <div class="flex items-center gap-2">
            <button type="button" :disabled="saving" @click="reload"
              class="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              <font-awesome-icon icon="sync" class="mr-2" />
              Reload
            </button>
            <button type="button" :disabled="saving" @click="save"
              class="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              <font-awesome-icon icon="save" class="mr-2" />
              {{ saving ? 'Saving…' : 'Save' }}
            </button>
          </div>
        </div>
      </div>

      <configuration-form v-model="form" />
    </div>
  </div>
</template>

<script>
import { gql } from 'graphql-tag';
import ConfigurationForm from './ConfigurationForm.vue';

export default {
  name: 'DefaultConfigurationTab',
  components: { ConfigurationForm },
  data() {
    return {
      loading: false,
      saving: false,
      errorMessage: '',
      resolved: null,
      form: null,
    };
  },
  mounted() {
    this.reload();
  },
  methods: {
    getTimetableDefaultConfigShape() {
      return {
        id: null,
        regular_start_time: '08:00',
        regular_end_time: '16:00',
        friday_start_time: '08:00',
        friday_end_time: '12:00',
        lunch_break_start: '12:00',
        lunch_break_end: '13:00',
        lecture_min_hours: 1.5,
        lecture_max_hours: 3.0,
        lab_min_hours: 6.0,
        lab_max_hours: 6.0,
        clinical_min_hours: 9.0,
        clinical_max_hours: 9.0,
        clinical_start_time: '06:30',
        clinical_end_time: '23:30'
      };
    },
    formatConfigTimeForInput(time) {
      if (time === null || time === undefined || time === '') {
        return '08:00';
      }
      var s = String(time).trim();
      if (!s) {
        return '08:00';
      }
      return s.length >= 5 ? s.substring(0, 5) : s;
    },
    ensureConfigTimeHms(t) {
      if (!t) {
        return '08:00:00';
      }
      var s = String(t);
      return s.length === 5 ? s + ':00' : s;
    },
    buildFormFromResolved() {
      var defaults = this.getTimetableDefaultConfigShape();
      var d = this.resolved;
      if (!d) {
        return defaults;
      }
      return {
        id: d.id || null,
        regular_start_time: this.formatConfigTimeForInput(d.regular_start_time),
        regular_end_time: this.formatConfigTimeForInput(d.regular_end_time),
        friday_start_time: this.formatConfigTimeForInput(d.friday_start_time),
        friday_end_time: this.formatConfigTimeForInput(d.friday_end_time),
        lunch_break_start: this.formatConfigTimeForInput(d.lunch_break_start),
        lunch_break_end: this.formatConfigTimeForInput(d.lunch_break_end),
        lecture_min_hours: parseFloat(d.lecture_min_hours) || 1.5,
        lecture_max_hours: parseFloat(d.lecture_max_hours) || 3.0,
        lab_min_hours: parseFloat(d.lab_min_hours) || 6.0,
        lab_max_hours: parseFloat(d.lab_max_hours) || 6.0,
        clinical_min_hours: parseFloat(d.clinical_min_hours) || 9.0,
        clinical_max_hours: parseFloat(d.clinical_max_hours) || 9.0,
        clinical_start_time: this.formatConfigTimeForInput(d.clinical_start_time),
        clinical_end_time: this.formatConfigTimeForInput(d.clinical_end_time),
      };
    },
    async reload() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const response = await this.$apollo.query({
          query: gql`
            query DefaultTimetableConfiguration($program_semester_id: ID) {
              timetableConfiguration(program_semester_id: $program_semester_id) {
                error
                message
                data {
                  id
                  program_semester_id
                  regular_start_time
                  regular_end_time
                  friday_start_time
                  friday_end_time
                  lunch_break_start
                  lunch_break_end
                  lecture_min_hours
                  lecture_max_hours
                  lab_min_hours
                  lab_max_hours
                  clinical_min_hours
                  clinical_max_hours
                  clinical_start_time
                  clinical_end_time
                }
              }
            }
          `,
          variables: { program_semester_id: null },
          fetchPolicy: 'network-only'
        });

        var wrap = response.data && response.data.timetableConfiguration;
        if (!wrap || wrap.error || !wrap.data) {
          this.resolved = null;
          this.form = this.getTimetableDefaultConfigShape();
          this.errorMessage = (wrap && wrap.message) ? String(wrap.message) : 'Could not load configuration.';
          return;
        }

        this.resolved = wrap.data;
        this.form = this.buildFormFromResolved();
      } catch (e) {
        this.resolved = null;
        this.form = this.getTimetableDefaultConfigShape();
        this.errorMessage = 'Could not load configuration.';
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.saving = true;
      this.errorMessage = '';
      try {
        var c = this.form;
        if (!c) {
          return;
        }
        var time = function (t) {
          return this.ensureConfigTimeHms(t);
        }.bind(this);

        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation SaveDefaultTimetableConfiguration($input: TimetableConfigurationInput!) {
              saveTimetableConfiguration(input: $input) {
                error
                message
                data { id }
              }
            }
          `,
          variables: {
            input: {
              id: c.id || null,
              program_semester_id: null,
              regular_start_time: time(c.regular_start_time),
              regular_end_time: time(c.regular_end_time),
              friday_start_time: time(c.friday_start_time),
              friday_end_time: time(c.friday_end_time),
              lunch_break_start: time(c.lunch_break_start),
              lunch_break_end: time(c.lunch_break_end),
              lecture_min_hours: c.lecture_min_hours,
              lecture_max_hours: c.lecture_max_hours,
              lab_min_hours: c.lab_min_hours,
              lab_max_hours: c.lab_max_hours,
              clinical_min_hours: c.clinical_min_hours,
              clinical_max_hours: c.clinical_max_hours,
              clinical_start_time: time(c.clinical_start_time),
              clinical_end_time: time(c.clinical_end_time),
            }
          }
        });

        var resSave = response.data && response.data.saveTimetableConfiguration;
        if (!resSave || resSave.error) {
          throw new Error((resSave && resSave.message) ? String(resSave.message) : 'Could not save.');
        }

        if (this.$swal) {
          this.$swal.fire({
            icon: 'success',
            title: 'Default configuration saved',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 2200
          });
        }

        await this.reload();
      } catch (err) {
        var msg = err && err.message ? String(err.message) : 'Could not save configuration.';
        if (this.$swal) {
          this.$swal.fire({ icon: 'error', title: 'Error', text: msg });
        } else {
          this.errorMessage = msg;
        }
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
